import Papa from 'papaparse';

import apiService from 'services';
import { readDataFile } from 'services/s3Service';

import {
  hasGeneratedDuplicateSuffix,
  normalizeToModelPeriod,
  parseYearMonthFromRaw,
} from './timePeriodParser';

export type TimeColumnMapping = {
  key: string;
  value: string | null;
  sourceValue: string;
  autoMapped: boolean;
};

export type TimeRowOption = {
  index: number;
  label: string;
  mappings: TimeColumnMapping[];
  matchedCount: number;
  detectedCount: number;
};

type LoadTimeRowOptionsParameters = {
  s3FileUrl: string;
  headerNames: string[];
  modelTimeRange: string[];
  dataSourceId?: number;
  versionId?: number;
  useApiPreview?: boolean;
};

type LoadTimeValueOptionsParameters = {
  s3FileUrl: string;
  headerNames: string[];
  timeColumn: string;
  modelTimeRange: string[];
  dataSourceId?: number;
  versionId?: number;
  useApiPreview?: boolean;
};

const fetchPreviewCsvText = async (
  dataSourceId: number,
  versionId: number,
  missingHeader = false,
): Promise<string> => {
  const response = await apiService.getVersionPreview(dataSourceId, versionId, {
    rows: 500,
    ...(missingHeader ? { missing_header: true } : {}),
  });
  const payload = response.data;
  const rows = payload?.rows || [];
  const columns = payload?.columns || [];
  return Papa.unparse(rows, { columns });
};

const rowNameFromCells = (row: Array<unknown>): string => {
  const first = row.find((cell) => cell != null && String(cell).trim() !== '');
  if (first == null) return 'Time period row';
  const name = String(first).trim();
  return name.length > 48 ? `${name.slice(0, 45)}…` : name;
};

const isTimePeriodLikeValue = (value: unknown, mappedPeriod: string | null): boolean => {
  if (mappedPeriod) return true;
  if (value == null) return false;
  const text = String(value).trim();
  return /^\d{4}$/.test(text) || parseYearMonthFromRaw(text) !== null;
};

const getColumnPeriodDisplayValue = (
  header: string,
  sourceValue: string,
  modelTimeRange: string[],
): string => {
  const trimmedHeader = String(header).trim();
  const trimmedSourceValue = sourceValue.trim();
  if (!hasGeneratedDuplicateSuffix(trimmedHeader)) return trimmedSourceValue || trimmedHeader;

  const headerPeriod = normalizeToModelPeriod(trimmedHeader, modelTimeRange);
  const sourcePeriod = normalizeToModelPeriod(trimmedSourceValue, modelTimeRange);
  if (headerPeriod && (!sourcePeriod || sourcePeriod === headerPeriod)) {
    return trimmedHeader;
  }
  return trimmedSourceValue || trimmedHeader;
};

const parseCsvToRowOptions = (
  csvText: string,
  headerNames: string[],
  modelTimeRange: string[],
): Promise<TimeRowOption[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: false,
      skipEmptyLines: false,
      complete: (result: Papa.ParseResult<unknown[]>) => {
        const allRows = (result.data as unknown[][]).filter((row) => Array.isArray(row));
        // Always include the first parsed row as a candidate. When the file has headers,
        // users often need to select that header row itself (e.g. Account | Dec 2023 | Jan 2024 ...).
        const startIndex = 0;
        const options = allRows
          .slice(startIndex)
          .map((row, offset) => {
            const actualIndex = startIndex + offset;
            const mappings = headerNames
              .map((header: string, columnIndex: number) => {
                const sourceValue = row[columnIndex] == null ? '' : String(row[columnIndex]).trim();
                const mappedPeriod = normalizeToModelPeriod(sourceValue, modelTimeRange);
                if (!header || !isTimePeriodLikeValue(sourceValue, mappedPeriod)) return null;
                return {
                  key: header,
                  value: mappedPeriod,
                  sourceValue: getColumnPeriodDisplayValue(header, sourceValue, modelTimeRange),
                  autoMapped: Boolean(mappedPeriod),
                };
              })
              .filter(Boolean) as TimeColumnMapping[];

            return {
              index: actualIndex,
              label: rowNameFromCells(row),
              mappings,
              matchedCount: mappings.length,
              detectedCount: mappings.length,
            };
          })
          .map((row) => ({
            ...row,
            matchedCount: row.mappings.filter((mapping) => mapping.value).length,
          }))
          .filter((row) => row.detectedCount > 0);
        resolve(options);
      },
      error: (error: Error) => reject(error),
    });
  });
};

const parseCsvToTimeValueOptions = (
  csvText: string,
  headerNames: string[],
  timeColumn: string,
  modelTimeRange: string[],
): Promise<TimeColumnMapping[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: false,
      skipEmptyLines: false,
      complete: (result: Papa.ParseResult<unknown[]>) => {
        const columnIndex = headerNames.indexOf(timeColumn);
        if (columnIndex < 0) {
          resolve([]);
          return;
        }

        const seenValues = new Set<string>();
        const allRows = (result.data as unknown[][]).filter((row) => Array.isArray(row));
        const mappings = allRows
          .map((row) => {
            const sourceValue = row[columnIndex] == null ? '' : String(row[columnIndex]).trim();
            const mappedPeriod = normalizeToModelPeriod(sourceValue, modelTimeRange);
            if (
              !sourceValue ||
              seenValues.has(sourceValue) ||
              !isTimePeriodLikeValue(sourceValue, mappedPeriod)
            ) {
              return null;
            }

            seenValues.add(sourceValue);
            return {
              key: sourceValue,
              value: mappedPeriod,
              sourceValue,
              autoMapped: Boolean(mappedPeriod),
            };
          })
          .filter(Boolean) as TimeColumnMapping[];

        resolve(mappings);
      },
      error: (error: Error) => reject(error),
    });
  });
};

export const loadTimeRowOptions = async ({
  s3FileUrl,
  headerNames,
  modelTimeRange,
  dataSourceId,
  versionId,
  useApiPreview = false,
}: LoadTimeRowOptionsParameters): Promise<TimeRowOption[]> => {
  let csvText: string;
  if (useApiPreview && dataSourceId && versionId) {
    csvText = await fetchPreviewCsvText(dataSourceId, versionId);
  } else {
    const key = s3FileUrl.split('/').pop();
    if (!key) {
      throw new Error('Missing file key');
    }
    const signedUrl = await readDataFile(key, 'blox-data-import');
    csvText = await fetch(signedUrl).then((response) => response.text());
  }
  return parseCsvToRowOptions(csvText, headerNames, modelTimeRange);
};

export const loadTimeValueOptions = async ({
  s3FileUrl,
  headerNames,
  timeColumn,
  modelTimeRange,
  dataSourceId,
  versionId,
  useApiPreview = false,
}: LoadTimeValueOptionsParameters): Promise<TimeColumnMapping[]> => {
  let csvText: string;
  if (useApiPreview && dataSourceId && versionId) {
    csvText = await fetchPreviewCsvText(dataSourceId, versionId);
  } else {
    const key = s3FileUrl.split('/').pop();
    if (!key) {
      throw new Error('Missing file key');
    }
    const signedUrl = await readDataFile(key, 'blox-data-import');
    csvText = await fetch(signedUrl).then((response) => response.text());
  }
  return parseCsvToTimeValueOptions(csvText, headerNames, timeColumn, modelTimeRange);
};
